import { useState } from 'react'
import { LogOut } from 'lucide-react'
import { Outlet } from 'react-router'
import { useAuth } from '@/auth/useAuth'
import { useMe } from '@/auth/useMe'
import { useMfaStatus } from '@/auth/useMfaStatus'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { MfaEnrolment } from '@/features/mfa/MfaEnrolment'
import { MfaStepUpDialog } from '@/features/mfa/MfaStepUpDialog'

/**
 * Blokkerende MFA-gate voor de staff-shell: zonder aal2 komt admin/team/
 * fundraiser de app niet in. Founders passeren `/` op weg naar /founder en
 * gaan hier ongemoeid doorheen; zij hebben `FounderMfaGate`.
 */
export function StaffMfaGate() {
  const { me, loading: meLoading } = useMe()
  const mfa = useMfaStatus()
  // De step-up-dialoog moet sluitbaar zijn: wie zijn authenticator kwijt is,
  // moet bij de uitlogknop in de GateShell kunnen komen (de modale overlay
  // blokkeert die anders). Annuleren sluit dus alleen de dialoog.
  const [stepUpOpen, setStepUpOpen] = useState(true)

  if (meLoading || mfa.loading) {
    return <GateSkeleton />
  }

  // Zonder rol kunnen we een founder niet uitsluiten. Doorlaten is dan veiliger
  // dan blokkeren: de aal2-gates in de backend blijven hoe dan ook gelden.
  if (!me || me.role === 'founder' || mfa.currentLevel === 'aal2') {
    return <Outlet />
  }

  if (!mfa.hasVerifiedFactor) {
    return (
      <GateShell title="Stel tweestapsverificatie in">
        <p className="text-sm text-muted-foreground">
          Tweestapsverificatie is verplicht voor het VONK-platform. Koppel een
          authenticator-app om verder te gaan.
        </p>
        <MfaEnrolment onVerified={() => mfa.refresh()} />
      </GateShell>
    )
  }

  return (
    <GateShell title="Bevestig dat jij het bent">
      <p className="text-sm text-muted-foreground">
        Voer de code uit je authenticator-app in om verder te gaan.
      </p>
      <MfaStepUpDialog
        open={stepUpOpen}
        onElevated={() => mfa.refresh()}
        onCancel={() => setStepUpOpen(false)}
      />
      {!stepUpOpen && (
        <div>
          <Button onClick={() => setStepUpOpen(true)}>Code invoeren</Button>
        </div>
      )}
    </GateShell>
  )
}

function GateShell({ title, children }: { title: string; children: React.ReactNode }) {
  const { signOut } = useAuth()
  return (
    <main className="flex min-h-svh items-center justify-center p-6">
      <section className="flex w-full max-w-2xl flex-col gap-4">
        <div className="vonk-card flex flex-col gap-3">
          <h1 className="font-heading text-2xl font-bold text-foreground">{title}</h1>
          {children}
        </div>
        <div>
          <Button variant="outline" onClick={() => void signOut()}>
            <LogOut className="size-4" />
            Uitloggen
          </Button>
        </div>
      </section>
    </main>
  )
}

function GateSkeleton() {
  return (
    <main className="flex min-h-svh items-center justify-center p-6" aria-hidden>
      <section className="flex w-full max-w-2xl flex-col gap-4">
        <Skeleton className="h-8 w-64" />
        <div className="vonk-card flex flex-col gap-3">
          <Skeleton className="h-5 w-2/3" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      </section>
    </main>
  )
}
